import React, { useEffect, useState } from 'react';
import { useI18n } from '../../i18n';
import { api } from '../../services/api';
import TryOnInfo from './TryOnInfo';

const framingOptions = ['full_body', 'upper_body', 'portrait', 'unknown'];

function formatBytes(value) {
  const bytes = Number(value || 0);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function readImage(file) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = URL.createObjectURL(file);
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

async function compressImage(file) {
  const image = await readImage(file);
  const maxSide = 1600;
  const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
  const width = Math.round(image.width * scale);
  const height = Math.round(image.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { alpha: false });
  context.drawImage(image, 0, 0, width, height);

  let blob = await canvasToBlob(canvas, 'image/webp', 0.86);
  let contentType = 'image/webp';

  if (!blob) {
    blob = await canvasToBlob(canvas, 'image/jpeg', 0.88);
    contentType = 'image/jpeg';
  }

  const imageData = await new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });

  URL.revokeObjectURL(image.src);

  return {
    image_data: imageData,
    content_type: contentType,
    width,
    height,
    file_size: blob.size,
  };
}

function moderationReasonKey(item) {
  const category = String(item?.category || '').toLowerCase();
  if (category.includes('sexual/minors')) return 'photos.error.reason.minors';
  if (category.includes('adult') || category.includes('sexual')) return 'photos.error.reason.adult';
  if (category.includes('racy')) return 'photos.error.reason.racy';
  if (category.includes('violence')) return 'photos.error.reason.violence';
  if (category.includes('self-harm')) return 'photos.error.reason.selfHarm';
  return '';
}

export default function UserPhotos({ embedded = false }) {
  const { t } = useI18n();
  const [photos, setPhotos] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [framingType, setFramingType] = useState('full_body');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [photoConsent, setPhotoConsent] = useState(false);
  const [photoConsentSaved, setPhotoConsentSaved] = useState(false);

  async function loadPhotos() {
    const response = await api.getUserPhotos();
    setPhotos(response.data || []);
  }

  useEffect(() => {
    loadPhotos().catch((err) => setError(err.message));
    api.getProfile()
      .then((response) => {
        const saved = Boolean(response.data?.preferences?.photo_processing_consented_at);
        setPhotoConsentSaved(saved);
        setPhotoConsent(saved);
      })
      .catch(() => {});
  }, []);

  async function uploadPhoto(event) {
    event.preventDefault();
    if (!selectedFile) return;

    setBusy(true);
    setError('');
    setNotice('');

    try {
      const compressed = await compressImage(selectedFile);
      await api.createUserPhoto({
        ...compressed,
        title: title || selectedFile.name,
        filename: selectedFile.name,
        framing_type: framingType,
        photo_processing_consent: photoConsent || photoConsentSaved,
      });
      setPhotoConsentSaved(true);
      setPhotoConsent(true);
      setSelectedFile(null);
      setTitle('');
      setNotice(t('photos.uploaded'));
      await loadPhotos();
    } catch (err) {
      const moderationKeys = (err.moderation || []).map(moderationReasonKey).filter(Boolean);
      if (err.message === 'photo_safety_rejected') {
        const reasonKey = moderationKeys[0] || 'photos.error.reason.generic';
        setError(t('photos.error.photo_safety_rejected_with_reason', { reason: t(reasonKey) }));
      } else {
        setError(err.message);
      }
    } finally {
      setBusy(false);
    }
  }

  async function setDefaultPhoto(id) {
    await api.updateUserPhoto(id, { is_default: true });
    await loadPhotos();
  }

  async function deletePhoto(id) {
    await api.deleteUserPhoto(id);
    await loadPhotos();
  }

  return (
    <div className={embedded ? 'ct-photos-page embedded' : 'ct-photos-page'}>
      {!embedded ? (
        <section className="ct-discover-hero ct-photos-hero">
          <div>
            <p className="ct-kicker">{t('photos.kicker')}</p>
            <h1>{t('photos.title')}</h1>
            <p>{t('photos.copy')}</p>
          </div>
        </section>
      ) : null}

      {error ? <div className="alert alert-danger">{t(`photos.error.${error}`) === `photos.error.${error}` ? error : t(`photos.error.${error}`)}</div> : null}
      {notice ? <div className="ct-profile-notice">{notice}</div> : null}

      <section className="ct-photo-upload">
        <form onSubmit={uploadPhoto}>
          <div>
            <h2>{t('photos.uploadTitle')}</h2>
            <p>{t('photos.uploadCopy')}</p>
            <div className="ct-photo-usage-note">
              <i className="ph ph-shield-check"></i>
              <span>
                {t('photos.usageNoteBefore')} <TryOnInfo /> {t('photos.usageNoteAfter')}
              </span>
            </div>
            {!photoConsentSaved ? (
              <label className="ct-photo-consent">
                <input
                  type="checkbox"
                  checked={photoConsent}
                  onChange={(event) => setPhotoConsent(event.target.checked)}
                />
                <span>{t('photos.processingConsent')}</span>
              </label>
            ) : null}
          </div>
          <label className="ct-photo-drop">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
            />
            <i className="ph ph-camera-plus"></i>
            <strong>{selectedFile ? selectedFile.name : t('photos.pickFile')}</strong>
            <span>{t('photos.optimizedBeforeUpload')}</span>
          </label>
          <div className="ct-photo-form-row">
            <input
              className="form-control"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={t('photos.titlePlaceholder')}
            />
            <select className="form-control" value={framingType} onChange={(event) => setFramingType(event.target.value)}>
              {framingOptions.map((option) => (
                <option value={option} key={option}>
                  {t(`photos.framing.${option}`)}
                </option>
              ))}
            </select>
            <button type="submit" disabled={!selectedFile || busy || (!photoConsent && !photoConsentSaved)}>
              {busy ? t('photos.uploading') : t('photos.upload')}
            </button>
          </div>
        </form>
      </section>

      <section className="ct-photo-grid">
        {photos.length === 0 ? (
          <div className="ct-card ct-empty">
            <h2>{t('photos.emptyTitle')}</h2>
            <p>{t('photos.emptyCopy')}</p>
          </div>
        ) : (
          photos.map((photo) => (
            <article className="ct-photo-card" key={photo.id}>
              <img src={photo.image_url} alt={photo.title || ''} />
              <div>
                <strong>{photo.title}</strong>
                <span>
                  {photo.width}x{photo.height} · {formatBytes(photo.file_size)}
                </span>
                <em>{t(`photos.framing.${photo.framing_type}`)}</em>
              </div>
              <div className="ct-photo-actions">
                {photo.is_default ? <span>{t('photos.default')}</span> : <button type="button" onClick={() => setDefaultPhoto(photo.id)}>{t('photos.makeDefault')}</button>}
                <button type="button" onClick={() => deletePhoto(photo.id)}>{t('common.remove')}</button>
              </div>
            </article>
          ))
        )}
      </section>
    </div>
  );
}
